import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useAppDispatch, useAppSelector } from '../../app/ReduxHooks.ts'
import { clearPaywall } from '../../features/BillingPaywallSlice.ts'
import {
  useClubAdminGetBillingStatusQuery,
  useGetClubDetailsQuery,
} from '../../features/SpeechAgendaApiSlice.ts'
import { useBillingRedirect } from '../../app/hooks/UseBillingRedirect.ts'
import { isInGoodStanding } from '../../utils/GoodStanding.ts'

const PaywallBanner = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const clubId = useAppSelector((state) => state.billingPaywall.clubId)

  // The 402 is club-scoped; once the user navigates away from that club's
  // pages the read-only warning no longer applies. Exact segment match, so
  // club 1's paywall doesn't survive on /club/10.
  useEffect(() => {
    const clubPath = `/club/${clubId}`
    if (
      clubId &&
      location.pathname !== clubPath &&
      !location.pathname.startsWith(`${clubPath}/`)
    ) {
      dispatch(clearPaywall())
    }
  }, [clubId, location.pathname, dispatch])

  const { data: clubDetails } = useGetClubDetailsQuery(clubId ?? '', {
    skip: !clubId,
  })
  const isAdmin = clubDetails?.roleId === 1
  const { data: billing } = useClubAdminGetBillingStatusQuery(clubId ?? '', {
    skip: !clubId || !isAdmin,
  })

  const { subscribe, manageSubscription, isBusy, error } = useBillingRedirect(
    clubId ?? ''
  )

  // A club in good standing can't be paywalled — e.g. someone opened a stale
  // ?checkout=cancel URL, or the subscription got fixed in another tab.
  const inGoodStanding = !!billing && isInGoodStanding(billing)

  if (!clubId || inGoodStanding) return null

  const neverSubscribed = billing ? !billing.subscriptionStatus : false

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 custom-max-w-920px">
      <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon
              className="h-5 w-5 flex-none text-amber-500 mt-0.5"
              aria-hidden="true"
            />
            <div>
              <p className="text-sm font-semibold text-amber-900">
                This club is read-only
              </p>
              <p className="text-sm text-amber-800">
                {isAdmin
                  ? neverSubscribed
                    ? 'Start your free trial to unlock changes - 90 days free, no card required, then $19/month per club. Cancel anytime'
                    : 'There is a problem with the club’s subscription. Fix the payment to unlock changes'
                  : 'The club’s subscription is inactive. Ask a club admin to renew it'}
              </p>
              {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
            </div>
          </div>
          {isAdmin && billing && (
            <button
              type="button"
              disabled={isBusy}
              onClick={() => void (neverSubscribed ? subscribe() : manageSubscription())}
              className="flex-none rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60">
              {neverSubscribed ? 'Start your free trial' : 'Fix payment'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default PaywallBanner
